import React from 'react';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  /** When set, shows error border + error focus-ring + helper text */
  error?: string;
  /** Optional trailing node (e.g. password-eye toggle) */
  trailingAffordance?: React.ReactNode;
}

// Base control classes — applied always
// Design system glass: campo translúcido (surface-field) com borda de campo, raio 12.
const BASE =
  'w-full bg-snapfy-field border border-snapfy-border-field rounded-input backdrop-blur-[16px] ' +
  'text-[13px] text-snapfy-text-primary px-3 py-2.5 outline-none ' +
  'placeholder:text-snapfy-text-placeholder ' +
  'transition-[box-shadow,border-color] duration-150';

// State modifier classes (appended as deltas on top of BASE)
const HOVER  = 'hover:border-snapfy-border-strong';
const FOCUS  = 'focus:border-snapfy-accent-border focus:ring-snapfy-focus';
const DISABLED = 'disabled:opacity-50 disabled:cursor-not-allowed';

// Error state: replaces focus ring with error-coloured ring.
// Documented exception — DESIGN_SYSTEM.md ambiguity #5: no --snapfy-error-ring token exists;
// rgba() form used instead of hex to satisfy the zero-hex grep gate.
const ERROR_BORDER = 'border-snapfy-error';
// Error focus-ring uses arbitrary box-shadow: rgba(239,68,68,0.4) — documented exception per
// DESIGN_SYSTEM.md §Declared ambiguities #5 (error focus-ring lacks a dedicated token).
const ERROR_FOCUS  = 'focus:border-snapfy-error focus:[box-shadow:0_0_0_2px_rgba(239,68,68,0.4)]';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled = false,
  error,
  trailingAffordance,
  id,
  className = '',
  ...rest
}, ref) => {
  // Generate a stable id for label association when none provided
  const inputId = id ?? (label ? `input-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

  const controlClasses = [
    BASE,
    HOVER,
    error ? `${ERROR_BORDER} ${ERROR_FOCUS}` : FOCUS,
    DISABLED,
    trailingAffordance ? 'pr-10' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-[13px] font-semibold text-snapfy-text-primary mb-1.5"
        >
          {label}
        </label>
      )}

      <div className="relative">
        <input
          {...rest}
          ref={ref}
          id={inputId}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error && inputId ? `${inputId}-error` : undefined}
          className={controlClasses}
        />

        {trailingAffordance && (
          <span
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-snapfy-text-secondary pointer-events-auto"
          >
            {trailingAffordance}
          </span>
        )}
      </div>

      {error && (
        <p
          id={inputId ? `${inputId}-error` : undefined}
          role="alert"
          className="text-xs text-snapfy-error mt-1.5"
        >
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
